import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  signal,
} from "@angular/core";
import { FormsModule } from "@angular/forms";
import { RouterModule } from "@angular/router";
import { Router } from "@angular/router";
import { rxResource } from "@angular/core/rxjs-interop";
import {
  catchError,
  forkJoin,
  map,
  of,
  switchMap,
} from "rxjs";
import { ApiService } from "../core/api.service";
import { Customer, CustomerInput, Room, WindowEntity } from "../core/models";

@Component({
  selector: "app-customer-create",
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [FormsModule, RouterModule],
  templateUrl: "./customer-create.component.html",
  styleUrls: ["./customer-create.component.scss"],
})
export class CustomerCreateComponent {
  form: CustomerInput = {
    name: "",
    email: "",
    phone: "",
    address: "",
    builder: "",
    model: "",
    elevation: "",
  };

  private readonly api = inject(ApiService);
  private readonly router = inject(Router);

  readonly message = signal("");
  readonly saving = signal(false);

  private readonly customersResource = rxResource({
    stream: () =>
      this.api.getCustomers().pipe(catchError(() => of([] as Customer[]))),
  });
  readonly customers = this.customersResource.value;

  readonly builderOptions = computed(() =>
    this.extractOptions(this.customers() ?? [], "builder"),
  );
  readonly modelOptions = computed(() =>
    this.extractOptions(this.customers() ?? [], "model"),
  );
  readonly elevationOptions = computed(() =>
    this.extractOptions(this.customers() ?? [], "elevation"),
  );

  private extractOptions(
    customers: Customer[],
    key: "builder" | "model" | "elevation",
  ) {
    const unique = new Set<string>();
    customers.forEach((customer) => {
      const value = (customer[key] ?? "").trim();
      if (value) {
        unique.add(value);
      }
    });
    return Array.from(unique).sort((a, b) => a.localeCompare(b));
  }

  save() {
    if (!this.form.name.trim()) {
      this.message.set("Customer name is required.");
      return;
    }
    this.saving.set(true);
    const payload = this.buildPayload();
    this.api
      .createCustomer(payload)
      .pipe(
        switchMap((created) =>
          this.cloneRoomsAndWindowsIfMatched(created).pipe(map(() => created)),
        ),
      )
      .subscribe({
        next: (created) => {
          this.saving.set(false);
          this.router.navigate(["/customers", created.id]);
        },
        error: () => {
          this.saving.set(false);
          this.message.set("Failed to create customer.");
        },
      });
  }

  private buildPayload(): CustomerInput {
    const name = this.form.name.trim();
    const email = this.form.email?.trim() || undefined;
    const phone = this.form.phone?.trim() || undefined;
    const address = this.form.address?.trim() || undefined;
    const builder = this.form.builder?.trim() || undefined;
    const model = this.form.model?.trim() || undefined;
    const elevation = this.form.elevation?.trim() || undefined;

    return {
      name,
      email,
      phone,
      address,
      builder,
      model,
      elevation,
    };
  }

  private cloneRoomsAndWindowsIfMatched(created: Customer) {
    const builder = this.form.builder?.trim();
    const model = this.form.model?.trim();
    const elevation = this.form.elevation?.trim();
    if (!builder || !model || !elevation) {
      return of(void 0);
    }

    const candidates = (this.customers() ?? []).filter(
      (customer) =>
        (customer.builder ?? "").trim() === builder &&
        (customer.model ?? "").trim() === model &&
        (customer.elevation ?? "").trim() === elevation,
    );

    if (!candidates.length) {
      return of(void 0);
    }

    return this.selectTemplateCustomer(candidates).pipe(
      switchMap((template) => {
        if (!template) return of(void 0);
        return this.cloneRoomsAndWindows(template.id, created.id);
      }),
      catchError(() => of(void 0)),
    );
  }

  private selectTemplateCustomer(candidates: Customer[]) {
    return forkJoin(
      candidates.map((customer) =>
        this.getWindowsCount(customer.id).pipe(
          map((windowsCount) => ({ customer, windowsCount })),
        ),
      ),
    ).pipe(
      map((rows) => {
        if (!rows.length) return null;
        return (
          rows.reduce((best, current) => {
            if (!best) return current;
            if (current.windowsCount > best.windowsCount) return current;
            if (current.windowsCount === best.windowsCount) {
              const bestDate = new Date(best.customer.createdAt).getTime();
              const currentDate = new Date(current.customer.createdAt).getTime();
              if (!Number.isNaN(currentDate) && currentDate > bestDate) {
                return current;
              }
            }
            return best;
          }, null as { customer: Customer; windowsCount: number } | null)
            ?.customer ?? null
        );
      }),
    );
  }

  private getWindowsCount(customerId: string) {
    return this.api
      .getCustomerWindows(customerId)
      .pipe(
        map((windows) => windows.length),
        catchError(() => of(0)),
      );
  }

  private cloneRoomsAndWindows(
    templateCustomerId: string,
    targetCustomerId: string,
  ) {
    return this.api.getRooms(templateCustomerId).pipe(
      switchMap((rooms) => {
        if (!rooms.length) return of(void 0);
        return this.api.getCustomerWindows(templateCustomerId).pipe(
          switchMap((templateWindows) =>
            forkJoin(
              rooms.map((room) =>
                this.api
                  .createRoom(targetCustomerId, this.toRoomInput(room))
                  .pipe(
                    switchMap((createdRoom) =>
                      this.cloneWindows(
                        createdRoom.id,
                        templateWindows.filter((w) => w.roomId === room.id),
                      ),
                    ),
                  ),
              ),
            ).pipe(map(() => void 0)),
          ),
          catchError(() => of(void 0)),
        );
      }),
    );
  }

  private toRoomInput(room: Room) {
    return {
      name: room.name,
      notes: room.notes,
    };
  }

  private cloneWindows(targetRoomId: string, windows: WindowEntity[]) {
    if (!windows.length) {
      return of(void 0);
    }
    return forkJoin(
      windows.map((window) =>
        this.api.createWindow(targetRoomId, {
          label: window.label,
          notes: window.notes,
          controlType: window.controlType,
          hasSolarPanel: window.hasSolarPanel,
        }),
      ),
    ).pipe(map(() => void 0));
  }
}
